import { memo, useCallback, useEffect, useRef } from 'react';
import useReoOpenCameraWhenHasError from '../../../features/exercise/hooks/use-reopen-camera-when-errors';
import { exercisesStats } from '../../../reducers/exercises-reducer';
import { useWindowDimensions } from '../../../hooks';
import { CAMERA_VIEW_MODES, useCameraViewMode } from '../../../zustand/camera-view-mode-store';
import {
  restoreCameraPreference,
  initializeCamera,
  getActiveCameraStream,
  useCameraManagerStore,
  logCamera,
} from '../../../services/camera-manager';
import tw from 'twin.macro';

const WebcamAndSkeltonSide = ({
  videoRef,
  dispatch,
  audioSectionRef,
  setDrawOnImage,
  setIsCameraTranslated,
  mediaSourceType,
  uploadedVideoUrl,
  videoRotation = 0,
  isStackedMobile,
  skeletonCanvasRef,
  // Unmounting the canvas (rather than hiding it) also stops usePredict from
  // drawing: it only draws when skeletonCanvasRef.current exists, so there are no
  // stale joints left on screen from before the toggle.
  showSkeleton = true,
}) => {
  const { clientWindowHeight } = useWindowDimensions();
  const isUploadedVideoSource = mediaSourceType === 'upload' && Boolean(uploadedVideoUrl);
  const isUsingCameraSource = !isUploadedVideoSource;

  // Picked in the pause modal and persisted, so it is read from the store here
  // rather than threaded through the exercise page. Only the live webcam honours
  // it: an uploaded clip is always shown whole (object-contain), which is what
  // the frame handed to MediaPipe is letterboxed to match.
  const cameraViewMode = useCameraViewMode(state => state.mode);
  // All three modes keep the same box; only how the frame is mapped into it
  // changes, which is why nothing here has to measure the stream.
  const cameraFitStyle =
    cameraViewMode === CAMERA_VIEW_MODES.contain
      ? tw`object-contain`
      : cameraViewMode === CAMERA_VIEW_MODES.fill
        ? tw`object-fill`
        : tw`object-cover`;

  const cameraTransform = `rotate(${videoRotation}deg) scaleX(-1)`;
  const boxedCameraHeight = clientWindowHeight - (audioSectionRef?.current?.clientHeight ?? 0);

  // The single camera <video> element for this component's whole lifetime —
  // CameraManager owns whether the stream behind it is reused from
  // camera-permission or freshly acquired, so unlike the old <Webcam
  // key={key}> trick this element itself never unmounts/remounts on recovery.
  const cameraVideoElementRef = useRef(null);

  const assignCameraVideoRef = element => {
    cameraVideoElementRef.current = element;
    if (!element) {
      videoRef.current = null;
      return;
    }

    // Expose the element immediately, even before any stream is attached —
    // react-webcam used to make its internal <video> available on videoRef
    // synchronously on mount too, well before getUserMedia resolved. Other
    // hooks (use-predict-worker's frame loop, use-media-aspect-ratio) poll
    // videoRef.current.video on an interval starting from mount, and without
    // this they see `null` and log "Video element not found" on every tick
    // for the entire acquisition window instead of just waiting on
    // `.video.readyState`, which they already handle.
    if (videoRef.current?.video !== element) {
      videoRef.current = { video: element, stream: videoRef.current?.stream ?? null };
    }
  };

  const assignUploadedVideoRef = element => {
    if (!element) {
      videoRef.current = null;
      return;
    }

    videoRef.current = {
      video: element,
      stream: null,
    };
  };

  // Whether the camera <video> still has a live video track. `muted` is
  // deliberately not part of "alive": iOS mutes a track during a transient
  // interruption and unmutes it again on its own, while reacquiring to escape
  // a mute costs a fresh getUserMedia — i.e. a permission prompt mid-session.
  // Only a track that is no longer `live` is really gone.
  const isCameraStreamAlive = useCallback(() => {
    const track = videoRef?.current?.stream?.getVideoTracks?.()[0];
    return Boolean(track) && track.readyState === 'live';
  }, [videoRef]);

  // Whether this component has ever held a live stream. A reacquire only holds
  // the socket closed (see `cameraReacquiring` in exercise/index.jsx) when a
  // stream that WAS working is being recovered — a first acquisition that simply
  // fails (permission denied, no device) is a settled answer, not a pending one,
  // and must not delay the session by the hold timeout.
  const hasHadLiveStreamRef = useRef(false);

  // Recovery was armed but the stream healed during the delay — put
  // cameraIsActive back, since whichever handler armed it dispatched false.
  const handleRecoveryCancelled = useCallback(() => {
    dispatch({ type: exercisesStats.cameraIsActive, payload: true });
    dispatch({ type: exercisesStats.cameraReacquiring, payload: false });
  }, [dispatch]);

  const attachCameraStream = useCallback(
    stream => {
      const element = cameraVideoElementRef.current;
      if (element && element.srcObject !== stream) {
        element.srcObject = stream ?? null;
      }
      videoRef.current = element ? { video: element, stream: stream ?? null } : null;
    },
    [videoRef]
  );

  // A track can die mid-exercise (OS/browser camera toggled off, device
  // unplugged, permission revoked) without ever going through a failed
  // acquisition — this only fires on acquisition failure. This does NOT fire
  // from our own stopCamera()'s track.stop() (the spec only fires `ended` for
  // involuntary loss, not a script-initiated stop), so it can't misfire on an
  // intentional exit/navigate-away.
  const wireTrackWatchers = useCallback(
    stream => {
      stream?.getTracks().forEach(track => {
        track.onended = () => {
          logCamera('track-ended', { trackId: track.id });
          dispatch({ type: exercisesStats.cameraIsActive, payload: false });
          // Told before the recovery is armed, so a socket that drops in the
          // same breath (a backgrounded tab loses both) waits for the camera
          // instead of reopening the session with no camera reported.
          dispatch({ type: exercisesStats.cameraReacquiring, payload: true });
          // Without this, cameraIsActive flips false (correctly dropping into
          // the offline send loop) but nothing ever asks for the camera
          // again. recoverCameraRef is assigned below, once the recovery hook
          // itself exists.
          recoverCameraRef.current('track-ended');
        };

        // iOS mutes tracks during transient interruptions (backgrounding,
        // another app briefly taking the camera) without ending them — that is
        // not a broken stream, so this only logs. A mute that never clears is
        // caught separately by the visibility health check below once the
        // track's own `ended`/`readyState` reflects it.
        track.onmute = () => logCamera('track-muted', { trackId: track.id });
        track.onunmute = () => logCamera('track-unmuted', { trackId: track.id });
      });
    },
    [dispatch]
  );

  const handleAcquired = useCallback(
    (stream, settings, source) => {
      logCamera('getUserMedia-success', { source, streamActive: stream?.active, ...settings });

      attachCameraStream(stream);
      hasHadLiveStreamRef.current = hasHadLiveStreamRef.current || Boolean(stream?.active);
      dispatch({ type: exercisesStats.cameraIsActive, payload: Boolean(stream?.active) });
      // Whatever was being recovered has landed — release the socket's hold.
      dispatch({ type: exercisesStats.cameraReacquiring, payload: false });
      // The real device state is now known — let the socket's config message
      // (see use-websocket-connection) stop waiting and report it, instead of
      // whatever stale value cameraIsActive was seeded with from localStorage.
      dispatch({ type: exercisesStats.cameraCheckSettled, payload: true });
      wireTrackWatchers(stream);
    },
    [attachCameraStream, dispatch, wireTrackWatchers]
  );

  const handleAcquisitionFailed = useCallback(
    (error, source) => {
      logCamera('getUserMedia-failure', {
        source,
        errorName: error?.name,
        errorMessage: error?.message,
      });

      // Same reasoning as the track-ended case above: a failed acquisition
      // must flip cameraIsActive right away, or the app keeps believing it
      // has a camera it doesn't, and neither the detection loop nor the
      // offline heartbeat loop ever takes over.
      dispatch({ type: exercisesStats.cameraIsActive, payload: false });
      dispatch({ type: exercisesStats.cameraCheckSettled, payload: true });
      // Still in flight for a camera that was working a moment ago (recovery
      // re-arms itself below); a first acquisition that failed is not — see
      // hasHadLiveStreamRef.
      dispatch({
        type: exercisesStats.cameraReacquiring,
        payload: hasHadLiveStreamRef.current,
      });
      recoverCameraRef.current('getUserMedia-error', error);
    },
    [dispatch]
  );

  // Broken by a ref rather than a direct reference: onRecover is handed to
  // the recovery hook below, but its failure branch needs to call the same
  // hook's own recoverCamera again to keep retrying — recoverCameraRef is
  // assigned right after the hook call, once recoverCamera actually exists.
  const recoverCameraRef = useRef(() => {});

  const onRecover = useCallback(async () => {
    // Retry the same device this component was last showing, not a bare
    // generic acquisition — CameraManager's own deviceId -> facingMode
    // fallback chain (services/camera/constraints.js) still applies
    // underneath if that specific device can't be reacquired as-is.
    const { currentDeviceId } = useCameraManagerStore.getState();
    const result = await initializeCamera({ deviceId: currentDeviceId });

    if (!result.ok) {
      handleAcquisitionFailed(result.error?.raw ?? result.error, 'recovery');
      return;
    }

    handleAcquired(getActiveCameraStream(), result.settings, 'recovery');
  }, [handleAcquired, handleAcquisitionFailed]);

  const { recoverCamera, cameraError } = useReoOpenCameraWhenHasError({
    isStreamAlive: isCameraStreamAlive,
    onRecoveryCancelled: handleRecoveryCancelled,
    onRecover,
  });
  recoverCameraRef.current = recoverCamera;

  // The one real acquisition on mount: reuse whatever camera-permission
  // already granted and persisted (restoreCameraPreference), falling back to
  // a plain generic acquisition only when there is genuinely no stored
  // preference to restore (e.g. permission was denied on /camera-permission,
  // or this page was reached by a direct link that skipped it).
  useEffect(() => {
    if (!isUsingCameraSource) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      let result = await restoreCameraPreference();

      if (!cancelled && result.reason === 'no-stored-preference') {
        result = await initializeCamera({});
      }

      if (cancelled) {
        return;
      }

      if (!result.ok) {
        handleAcquisitionFailed(result.error?.raw ?? result.error, 'mount');
        return;
      }

      handleAcquired(getActiveCameraStream(), result.settings, 'mount');
    })();

    return () => {
      cancelled = true;
    };
    // Runs once for this component's lifetime — the camera branch is never
    // remounted (see the single-owner comment in exercise/index.jsx).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUsingCameraSource]);

  // iOS can end/mute the capture session without any React-visible error
  // while the tab is backgrounded. Re-check the current track once the page
  // is foregrounded again instead of assuming a healthy stream stayed that way.
  useEffect(() => {
    if (!isUsingCameraSource) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      const stream = videoRef?.current?.stream;
      const track = stream?.getVideoTracks?.()[0];
      const alive = Boolean(track) && track.readyState === 'live';

      logCamera('visibility-visible', {
        streamActive: stream?.active,
        trackReadyState: track?.readyState,
        trackEnabled: track?.enabled,
        trackMuted: track?.muted,
        trackId: track?.id,
      });

      if (!alive && !cameraError) {
        dispatch({ type: exercisesStats.cameraIsActive, payload: false });
        dispatch({ type: exercisesStats.cameraReacquiring, payload: true });
        recoverCamera('visibility-health-check');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [cameraError, dispatch, isUsingCameraSource, recoverCamera, videoRef]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* Deliberately NOT gated on `cameraError`. It used to be, and that meant
          the media element was unmounted for the whole RECOVERY_DELAY_MS window
          — which used to run <Webcam>'s componentWillUnmount, stopping the
          tracks. So by the time recovery fired, a stream that had only been
          transiently interrupted was already dead by our own hand, and the
          reacquire (a fresh getUserMedia, i.e. an iOS permission prompt) was
          unavoidable. Keeping the element mounted lets the interruption heal and
          lets isStreamAlive cancel the reacquire. The reacquire itself is still
          driven imperatively by onRecover above, so real recoveries are
          unaffected. */}
      <div style={isStackedMobile ? { height: '100%' } : undefined}>
        {isUploadedVideoSource ? (
          <video
            preload="metadata"
            ref={assignUploadedVideoRef}
            // object-contain, not cover: an uploaded clip is shown whole so
            // nothing is cropped away, and the frame sent to MediaPipe is
            // letterboxed the same way (see drawImageContain in
            // use-predict-worker) — so the skeleton overlay still lines up.
            tw="w-full h-full z-[0] object-contain bg-black"
            style={
              isStackedMobile
                ? undefined
                : { height: clientWindowHeight - audioSectionRef?.current?.clientHeight }
            }
            src={uploadedVideoUrl}
            muted
            loop
            controls
            playsInline
            autoPlay={false}
            onCanPlay={event => {
              dispatch({
                type: exercisesStats.cameraIsActive,
                payload: true,
              });
              setDrawOnImage(true);
              setIsCameraTranslated(true);
            }}
            onPause={() => {}}
            onPlay={() => {
              dispatch({
                type: exercisesStats.cameraIsActive,
                payload: true,
              });
            }}
            onEnded={() => {
              dispatch({
                type: exercisesStats.cameraIsActive,
                payload: false,
              });
            }}
          />
        ) : (
          <video
            ref={assignCameraVideoRef}
            // The fit is the camera-view setting (pause modal): crop to the box
            // (default), letterbox inside it (contain), or stretch onto it
            // (fill). The box itself is the same in all three.
            css={[tw`w-full h-full z-[0] bg-black`, cameraFitStyle]}
            style={
              isStackedMobile
                ? { transform: cameraTransform }
                : { height: boxedCameraHeight, transform: cameraTransform }
            }
            autoPlay
            muted
            playsInline
            onCanPlay={() => {
              setDrawOnImage(true);
              setIsCameraTranslated(true);
            }}
          />
        )}
      </div>

      {/* Live pose skeleton overlay — matches the webcam's box + mirror
          transform so the drawn joints line up with the mirrored video. The
          usePredict hook draws onto this canvas every frame via skeletonCanvasRef. */}
      {showSkeleton && !cameraError && (
        <canvas
          ref={skeletonCanvasRef}
          tw="pointer-events-none absolute top-0 left-0 w-full z-[1]"
          style={{
            height: isStackedMobile ? '100%' : boxedCameraHeight,
            // The mirror only exists to match the webcam, which is rendered
            // flipped. An uploaded clip is played as-is, so mirroring the
            // overlay there would put the skeleton on the wrong side.
            transform: isUploadedVideoSource ? `rotate(${videoRotation}deg)` : cameraTransform,
          }}
        />
      )}
    </div>
  );
};
export default memo(WebcamAndSkeltonSide);
